import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, Outlet } from '@tanstack/react-router';

import { useAuthStore } from '../stores/auth';
import { useWsStore } from '../stores/ws';
import { useProjectStore } from '../stores/project';
import { shaktiApi } from '../api/client';
import { ProjectSwitcher } from '../components/NavSidebar';
import { CommandPalette } from '../components/CommandPalette';

/**
 * Shell layout rendered for every route.
 *
 * - Top nav: product wordmark, environment badge, WS status, user menu.
 * - Side nav: six Wave-2 destinations. Each link uses the TanStack
 *   <Link> component so prefetching and active-state tracking come for
 *   free.
 * - <Outlet/> hosts the concrete view.
 */
export function RootRoute(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const connection = useWsStore((s) => s.connection);
  const token = useAuthStore((s) => s.token);
  const organizationId = useAuthStore((s) => s.organizationId);
  const setWsTicket = useAuthStore((s) => s.setWsTicket);
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const [trust, setTrust] = useState<{ backend: string; verified: boolean } | null>(null);
  const [approvalsPending, setApprovalsPending] = useState<number>(0);

  // #118 — mint a WS ticket each time auth or active project changes.
  // The backend issues a 10-minute TTL; we refresh every 8 minutes.
  useEffect(() => {
    if (!token || !organizationId) return;
    let cancelled = false;
    const mint = async (): Promise<void> => {
      try {
        const resp = await shaktiApi.mintWsTicket({
          organization_id: organizationId,
          ...(activeProjectId ? { project_id: activeProjectId } : {}),
        });
        if (!cancelled) setWsTicket(resp.token, resp.expires_at);
      } catch {
        if (!cancelled) setWsTicket(null, null);
      }
    };
    void mint();
    const t = setInterval(() => void mint(), 8 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [token, organizationId, activeProjectId, setWsTicket]);

  // #116 — fetch Trust L0 attestation snapshot once at boot.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    shaktiApi
      .getTrustStatus()
      .then((s) => {
        if (!cancelled) setTrust({ backend: s.backend, verified: s.verified });
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // #113 — poll pending approvals count for the ambient nav badge.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const tick = async (): Promise<void> => {
      try {
        const resp = await fetch('/v1/approvals/count', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const json = (await resp.json()) as { pending: number };
        if (!cancelled) setApprovalsPending(json.pending ?? 0);
      } catch {
        /* best-effort */
      }
    };
    void tick();
    const t = setInterval(() => void tick(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [token]);

  return (
    <div className="flex min-h-screen flex-col bg-bg-base text-fg-primary">
      {/*
        K4.5 (round-5) — skip links. Keyboard users tab into these
        before any nav, so screen-reader and keyboard users don't have
        to wade through the top nav + sidebar on every page. They
        reveal themselves on focus via `.shakti-skip-link`.
      */}
      <a href="#main" className="shakti-skip-link">
        Skip to main content
      </a>
      <a href="#primary-nav" className="shakti-skip-link">
        Skip to primary navigation
      </a>

      <header className="shakti-topnav" role="banner">
        <Link
          to="/"
          className="flex items-center gap-2 text-base font-semibold tracking-tight"
          aria-label="Shakti home"
        >
          <span
            aria-hidden="true"
            className="grid h-7 w-7 place-items-center rounded-md bg-accent-muted text-accent"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7h16M4 12h16M4 17h10" strokeLinecap="round" />
            </svg>
          </span>
          Shakti
          <span className="rounded-xs bg-bg-raised px-1.5 py-0.5 text-2xs font-mono text-fg-muted">
            dashboard
          </span>
        </Link>

        <nav
          aria-label="Breadcrumb"
          className="ml-2 hidden items-center gap-2 text-sm text-fg-muted md:flex"
        >
          <span className="shakti-mono text-2xs uppercase tracking-wider">
            enterprise software evolution platform
          </span>
        </nav>

        <div className="ml-auto flex items-center gap-4">
          <ProjectSwitcher />
          {trust && (
            <span
              role="status"
              title={`Trust L0 backend: ${trust.backend}`}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider ${
                trust.verified
                  ? 'bg-status-success/20 text-status-success'
                  : 'bg-status-warning/20 text-status-warning'
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              trust · {trust.backend}
            </span>
          )}
          <ConnectionBadge status={connection.status} />
          {user ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-fg-secondary">{user.email}</span>
              <button
                type="button"
                onClick={logout}
                className="rounded-sm border border-border-subtle px-2 py-1 text-xs text-fg-secondary transition-colors hover:border-border-strong hover:text-fg-primary"
              >
                Sign out
              </button>
            </div>
          ) : (
            <span className="text-xs text-fg-muted">Signed out</span>
          )}
        </div>
      </header>

      <div className="flex flex-1">
        <aside id="primary-nav" className="shakti-sidenav" aria-label="Primary navigation">
          <SideNavSection label="Overview">
            <SideNavLink to="/" label="Home" exact />
            <SideNavLink to="/dashboard" label="Command Center" />
          </SideNavSection>
          <SideNavSection label="Pipeline">
            <SideNavLink to="/dashboard/lifecycle" label="Lifecycle" />
            <SideNavLink to="/dashboard/artifacts" label="Artifacts" />
            <SideNavLink to="/dashboard/dependencies" label="Dependencies" />
            <SideNavLink to="/dashboard/impact" label="Impact" />
            <SideNavLink to="/dashboard/planning-chat" label="Planning chat" />
          </SideNavSection>
          <SideNavSection label="Operations">
            <SideNavLink to="/dashboard/activity" label="Activity" />
            <SideNavLink
              to="/dashboard/approvals"
              label="Approvals"
              badge={approvalsPending > 0 ? approvalsPending : undefined}
            />
            <SideNavLink to="/dashboard/deploy" label="Deploy" />
            <SideNavLink to="/dashboard/audit" label="Audit trail" />
            <SideNavLink to="/dashboard/costs" label="Costs" />
            <SideNavLink to="/dashboard/rate-limits" label="Rate limits" />
            <SideNavLink to="/dashboard/data-residency" label="Data residency" />
          </SideNavSection>
          <SideNavSection label="Setup">
            <SideNavLink to="/dashboard/settings" label="Settings" />
            <SideNavLink to="/dashboard/marketing" label="Marketing leads" />
          </SideNavSection>
          <div className="mt-auto px-3 py-4 text-2xs text-fg-muted">
            <span className="shakti-mono">v0.1.0 shell</span>
          </div>
        </aside>

        <main id="main" role="main" className="flex-1 px-8 py-8">
          <Outlet />
        </main>
      </div>

      {/* K4.1 (round-5) — global ⌘K/Ctrl-K command palette. */}
      <CommandPalette />
    </div>
  );
}

type NavLinkPath =
  | '/'
  | '/dashboard'
  | '/dashboard/lifecycle'
  | '/dashboard/artifacts'
  | '/dashboard/dependencies'
  // glistening-zebra Phase F1 — new pipeline-section routes.
  | '/dashboard/impact'
  | '/dashboard/planning-chat'
  | '/dashboard/activity'
  | '/dashboard/approvals'
  | '/dashboard/settings'
  | '/dashboard/deploy'
  | '/dashboard/audit'
  | '/dashboard/costs'
  | '/dashboard/rate-limits'
  | '/dashboard/marketing'
  | '/dashboard/data-residency';

interface SideNavLinkProps {
  to: NavLinkPath;
  label: string;
  exact?: boolean;
  /** #113 (round-6 close-out) — ambient badge (e.g. pending approvals count). */
  badge?: number;
}

function SideNavLink({ to, label, exact = false, badge }: SideNavLinkProps): JSX.Element {
  return (
    <Link
      to={to}
      className="shakti-sidenav-link"
      activeOptions={{ exact }}
      activeProps={{ 'data-active': 'true' }}
    >
      <span className="flex-1">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span
          aria-label={`${badge} pending`}
          className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-status-warning/20 px-1.5 text-2xs font-semibold text-status-warning"
        >
          {badge}
        </span>
      )}
    </Link>
  );
}

interface SideNavSectionProps {
  label: string;
  children: ReactNode;
}

function SideNavSection({ label, children }: SideNavSectionProps): JSX.Element {
  return (
    <div className="mb-2">
      <div className="px-3 pb-1 text-2xs font-semibold uppercase tracking-wider text-fg-muted">
        {label}
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

interface ConnectionBadgeProps {
  status: 'connecting' | 'open' | 'closed' | 'errored';
}

function ConnectionBadge({ status }: ConnectionBadgeProps): JSX.Element {
  const label = {
    connecting: 'Connecting…',
    open: 'Live',
    closed: 'Offline',
    errored: 'Error',
  }[status];

  const tone = {
    connecting: 'bg-status-warning/15 text-status-warning',
    open: 'bg-status-success/15 text-status-success',
    closed: 'bg-bg-raised text-fg-muted',
    errored: 'bg-status-error/15 text-status-error',
  }[status];

  return (
    <span
      className={`rounded-sm px-2 py-0.5 text-2xs font-medium uppercase tracking-wider ${tone}`}
      role="status"
      aria-live="polite"
    >
      {label}
    </span>
  );
}
